import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Hourglass, ChevronRight, ChevronDown, X } from "lucide-react";
import { PL } from "@/components/PL";

const natKey = (planets, lord) => String(planets?.[lord]?.nature || "").toLowerCase();
const yr = (s) => { try { return new Date(s * 1000).getUTCFullYear(); } catch { return ""; } };
const cleanDate = (s, tz) => { try { return new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short", year: "numeric" }).format(new Date(s * 1000)); } catch { return ""; } };

const MEANING = {
  Ketu: "Detachment, spiritual turns, letting go.",
  Venus: "Relationships, comfort, creativity, prosperity.",
  Sun: "Authority, recognition, father, vitality.",
  Moon: "Mind, emotions, home, nurturing.",
  Mars: "Drive, courage, property, conflict.",
  Rahu: "Ambition, change, foreign, sudden rise.",
  Jupiter: "Wisdom, growth, teachers, fortune.",
  Saturn: "Discipline, delay, hard-won results, karma.",
  Mercury: "Intellect, commerce, communication, learning.",
};

function Badge({ lord, planets, size = "sm", pt = (s) => s }) {
  const cls = size === "lg" ? "px-3 py-1 text-xs" : "px-2.5 py-0.5 text-[11px]";
  const nat = natKey(planets, lord);
  return <span className={`pd-nat ${cls}`} data-nat={["good", "bad", "node"].includes(nat) ? nat : ""}>{pt(lord)}</span>;
}

// Full progress bar with % label (running-period hero).
function Progress({ start, end, now, nat }) {
  const span = Math.max(1, end - start);
  const pct = Math.min(100, Math.max(0, ((now - start) / span) * 100));
  return (
    <div className="mt-3 flex items-center gap-2">
      <div className="pd-track h-1.5 flex-1 overflow-hidden rounded-full">
        <div className="pd-bar h-full rounded-full" data-nat={["good", "bad", "node"].includes(nat) ? nat : ""} style={{ width: `${pct}%` }} />
      </div>
      <span className="pd-ink3 text-[10px] tabular-nums">{Math.round(pct)}%</span>
    </div>
  );
}

// Compact per-period progress for list/sheet rows. 0% (future), 100% (past) or
// the live fraction when the period is currently running.
function ProgressMini({ start, end, now, nat }) {
  const span = Math.max(1, end - start);
  const pct = Math.min(100, Math.max(0, ((now - start) / span) * 100));
  const done = now >= end;
  return (
    <div className="pd-track mt-2 h-1 w-full overflow-hidden rounded-full" aria-hidden>
      <div className="pd-bar h-full rounded-full" data-nat={["good", "bad", "node"].includes(nat) ? nat : ""} style={{ width: `${done ? 100 : pct}%`, opacity: done ? 0.35 : 1 }} />
    </div>
  );
}

function remaining(end, now) {
  let s = Math.max(0, end - now);
  const y = Math.floor(s / (365.25 * 86400)); s -= y * 365.25 * 86400;
  const m = Math.floor(s / (30.4 * 86400));
  return y > 0 ? `${y}y ${m}m left` : `${m}m left`;
}

// Bottom-sheet drill-down (70-75vh). Closes four ways: X button, tap outside,
// swipe down, and the browser Back button (a history entry is pushed on open so
// Back pops this sheet first instead of leaving the tab).
function BottomSheet({ title, subtitle, onClose, children }) {
  const [drag, setDrag] = useState(0);
  const startY = useRef(null);

  // Push one history entry so the hardware/browser Back button closes the sheet.
  useEffect(() => {
    window.history.pushState({ dashaSheet: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any explicit close routes through Back so the pushed entry is cleaned up.
  const requestClose = useCallback(() => {
    if (window.history.state?.dashaSheet) window.history.back();
    else onClose();
  }, [onClose]);

  const onTouchStart = (e) => { startY.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setDrag(dy);
  };
  const onTouchEnd = () => {
    if (drag > 110) requestClose();
    setDrag(0); startY.current = null;
  };

  return createPortal(
    <div className="plux-modal fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={requestClose} data-testid="dasha-sheet-overlay">
      <div
        className="flex h-[74vh] w-full max-w-lg flex-col rounded-t-3xl border-t border-white/15 bg-[#121829] shadow-2xl pb-[env(safe-area-inset-bottom)]"
        style={{ transform: `translateY(${drag}px)`, transition: drag ? "none" : "transform 0.25s ease-out" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="dasha-sheet"
      >
        {/* grab handle — also a swipe-down target */}
        <div className="flex shrink-0 cursor-grab justify-center pt-3 active:cursor-grabbing" data-testid="dasha-sheet-handle"
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <span className="h-1.5 w-12 rounded-full bg-white/25" />
        </div>
        {/* header */}
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-3">
          <div className="min-w-0">
            <h3 className="font-serif-latin text-lg leading-tight text-white">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-white/55">{subtitle}</p>}
          </div>
          <button onClick={requestClose} data-testid="dasha-sheet-close" aria-label="Close" className="shrink-0 rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {/* scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-8">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export default function AdvancedDasha({ dasha, pt = (s) => s }) {
  const [openMd, setOpenMd] = useState(null);
  const [openAd, setOpenAd] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (!dasha?.mahadashas) return null;
  const tz = dasha.tz || "Asia/Kolkata";
  const planets = dasha.planets;
  const cur = dasha.current || {};
  const now = dasha.at ? dasha.at : Date.now() / 1000;
  const running = (s, e) => now >= s && now < e;
  const mds = dasha.mahadashas;
  const span0 = mds[0]?.start, spanN = mds[mds.length - 1]?.end;
  const total = Math.max(1, (spanN || 0) - (span0 || 0));

  const curIdx = Math.max(0, mds.findIndex((m) => m.isCurrent || running(m.start, m.end)));
  const visible = showAll ? mds.map((m, i) => [m, i]) : mds.map((m, i) => [m, i]).slice(curIdx, curIdx + 3);

  const md = openMd != null ? mds[openMd] : null;
  const ads = md ? (md.antardashas || []) : [];

  const closeMd = () => { setOpenMd(null); setOpenAd(null); };

  return (
    <div data-testid="advanced-dasha">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-400/10 ring-1 ring-amber-400/25"><Hourglass className="pd-accent h-5 w-5" /></span>
        <div>
          <h2 className="pd-ink font-serif-latin text-2xl leading-tight"><PL>Vimshottari Dasha</PL></h2>
          <p className="pd-ink3 text-sm"><PL>Running planetary periods — tap a period to drill in</PL></p>
        </div>
      </div>

      {/* Running hero */}
      <div className="pd-running mb-7 rounded-2xl p-5">
        <p className="pd-accent flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" /></span>
          <PL>Running now</PL>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <Badge lord={cur.mahadasha?.lord} planets={planets} size="lg" pt={pt} />
          <ChevronRight className="pd-ink3 h-4 w-4" />
          <Badge lord={cur.antardasha?.lord} planets={planets} size="lg" pt={pt} />
          <ChevronRight className="pd-ink3 h-4 w-4" />
          <Badge lord={cur.pratyantar?.lord} planets={planets} size="lg" pt={pt} />
        </div>
        {cur.mahadasha?.start != null && (
          <>
            <Progress start={cur.mahadasha.start} end={cur.mahadasha.end} now={now} nat={natKey(planets, cur.mahadasha?.lord)} />
            <div className="pd-ink3 mt-1.5 flex justify-between text-[11px] tabular-nums">
              <span>{cleanDate(cur.mahadasha.start, tz)} – {cleanDate(cur.mahadasha.end, tz)}</span>
              <span>{remaining(cur.mahadasha.end, now)}</span>
            </div>
          </>
        )}
        {cur.mahadasha?.lord && MEANING[cur.mahadasha.lord] && (
          <p className="pd-ink2 pd-line mt-4 border-t pt-3 text-xs leading-relaxed"><b className="pd-ink">{pt(cur.mahadasha.lord)} <PL>Mahadasha</PL>:</b> <PL>{MEANING[cur.mahadasha.lord]}</PL>{cur.antardasha?.lord && MEANING[cur.antardasha.lord] ? <> <b className="pd-ink">{pt(cur.antardasha.lord)} <PL>Antar</PL>:</b> <PL>{MEANING[cur.antardasha.lord]}</PL></> : null}</p>
        )}
      </div>

      {/* Proportional 120-year timeline */}
      <p className="pd-ink3 mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]"><PL>120-year timeline</PL></p>
      <div className="mb-2 flex h-11 w-full overflow-hidden rounded-xl" data-testid="dasha-timeline">
        {mds.map((m, i) => {
          const run = m.isCurrent || running(m.start, m.end);
          const w = ((m.end - m.start) / total) * 100;
          return (
            <button key={i} onClick={() => { setOpenMd(i); setOpenAd(null); }} title={`${m.lord} · ${yr(m.start)}–${yr(m.end)}`}
              className={`flex h-full items-center justify-center border-r border-black/20 text-[10px] font-bold last:border-r-0 ${run ? "pd-seg-now" : "pd-seg"}`}
              style={{ width: `${w}%` }}>
              <span className="truncate px-0.5">{w > 6 ? (m.lord || "").slice(0, 2) : ""}</span>
            </button>
          );
        })}
      </div>
      <div className="pd-ink3 mb-7 flex justify-between text-[10px] tabular-nums"><span>{yr(span0)}</span><span>birth → now → {yr(spanN)}</span></div>

      {/* Mahadasha list — current + next 2, drill into sheet */}
      <div className="mb-3 flex items-center justify-between">
        <p className="pd-ink3 text-[11px] font-semibold uppercase tracking-[0.18em]"><PL>Mahadasha periods</PL></p>
        <button onClick={() => setShowAll((v) => !v)} data-testid="dasha-view-all" className="pd-accent rounded-full border border-current/40 px-3 py-1 text-[11px] font-medium"><PL>{showAll ? "Show less" : "View full timeline"}</PL></button>
      </div>
      <div className="space-y-2.5">
        {visible.map(([m, i]) => {
          const run = m.isCurrent || running(m.start, m.end);
          return (
            <button key={i} onClick={() => { setOpenMd(i); setOpenAd(null); }} data-testid={`md-${i}`}
              className={`flex w-full items-center gap-3 rounded-2xl p-4 text-left ${run ? "pd-surf pd-active" : "pd-surf"}`}>
              <Badge lord={m.lord} planets={planets} size="lg" pt={pt} />
              <div className="min-w-0 flex-1">
                <p className="pd-ink text-sm font-medium">{pt(m.lord)} <PL>Mahadasha</PL></p>
                <p className="pd-ink3 text-[11px] tabular-nums">{cleanDate(m.start, tz)} – {cleanDate(m.end, tz)}</p>
                <ProgressMini start={m.start} end={m.end} now={now} nat={natKey(planets, m.lord)} />
              </div>
              {run && <span className="pd-pill rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"><PL>now</PL></span>}
              <ChevronRight className="pd-ink3 h-4 w-4 shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Single bottom-sheet: Antardashas, each expands its Pratyantars inline */}
      {md && (
        <BottomSheet
          title={<>{pt(md.lord)} <PL>Mahadasha</PL></>}
          subtitle={<><PL>Antardashas</PL> · {cleanDate(md.start, tz)} – {cleanDate(md.end, tz)}</>}
          onClose={closeMd}
        >
          <div className="space-y-2">
            {ads.map((a, ai) => {
              const run = a.isCurrent || running(a.start, a.end);
              const open = openAd === ai;
              const prats = a.pratyantars || a.pratyantardashas || [];
              return (
                <div key={ai} className={`rounded-xl border ${run ? "border-amber-400/60 bg-amber-400/[0.08]" : "border-white/10 bg-white/[0.03]"}`}>
                  <button onClick={() => setOpenAd(open ? null : ai)} data-testid={`ad-${ai}`}
                    className="flex w-full items-center gap-3 p-3 text-left">
                    <Badge lord={a.lord} planets={planets} pt={pt} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white/90">{pt(a.lord)} <PL>Antardasha</PL></p>
                      <p className="text-[11px] tabular-nums text-white/50">{cleanDate(a.start, tz)} – {cleanDate(a.end, tz)}</p>
                      <ProgressMini start={a.start} end={a.end} now={now} nat={natKey(planets, a.lord)} />
                    </div>
                    {run && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-300"><PL>now</PL></span>}
                    {prats.length > 0 && <ChevronDown className={`h-4 w-4 shrink-0 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />}
                  </button>
                  {open && prats.length > 0 && (
                    <ul className="space-y-1.5 border-t border-white/10 px-3 py-3" data-testid={`ad-${ai}-prats`}>
                      <li className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">{a.lord} › Pratyantardashas</li>
                      {prats.map((p, pi) => {
                        const prun = p.isCurrent || running(p.start, p.end);
                        return (
                          <li key={pi} className={`flex items-center gap-3 rounded-lg border p-2.5 ${prun ? "border-amber-400/60 bg-amber-400/10" : "border-white/8 bg-white/[0.02]"}`}>
                            <Badge lord={p.lord} planets={planets} pt={pt} />
                            <span className="flex-1 text-sm text-white/85">{pt(p.lord)} <PL>Pratyantar</PL></span>
                            <span className="text-[11px] tabular-nums text-white/55">{cleanDate(p.start, tz)} – {cleanDate(p.end, tz)}</span>
                            {prun && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300"><PL>now</PL></span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
            {ads.length === 0 && <p className="py-6 text-center text-sm text-white/45"><PL>No antardasha data for this period.</PL></p>}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
